import { useState } from "react";
import {
  Bell,
  BellOff,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageSquare,
  RefreshCw,
  Target,
  TriangleAlert,
  User,
} from "lucide-react";
import {
  DIRECTION_CONFIG,
  PRIORITY_CONFIG,
  RESOLUTION_CONFIG,
  RISK_CONFIG,
  SATISFACTION_CONFIG,
  contactLabel,
  describe,
  formatPhone,
  formatScore,
  relativeTime,
  scoreColor,
} from "./analysisLabels";
import AnalysisCoverage from "./AnalysisCoverage";

const Badge = ({ config }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${config.badge}`}>
    {config.label}
  </span>
);

const List = ({ title, items, icon: Icon, tone }) =>
  items.length ? (
    <div>
      <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <Icon className={`h-3.5 w-3.5 ${tone}`} />
        {title}
      </p>
      <ul className="space-y-1">
        {items.map((item, index) => (
          <li
            key={index}
            className="flex gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <span className={tone}>•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

const PriorityCard = ({
  item,
  onViewConversation,
  onOpenConversation,
  onReanalyze,
  onToggleAutoAnalysis,
  reanalyzing,
  togglingAutoAnalysis,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { analysis, priority } = item;
  const config = PRIORITY_CONFIG[priority.level] || PRIORITY_CONFIG.estavel;
  const signals = analysis.dissatisfactionSignals || [];
  const actions = analysis.recommendedActions || [];
  const topics = analysis.keyTopics || [];
  const agents = analysis.agentBehavior?.agents || [];
  const name = contactLabel(item.contact);
  const phone = formatPhone(item.contact?.phoneNumber);

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900 ${config.edge}`}
    >
      <div className="p-4">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate font-semibold text-gray-900 dark:text-gray-100">
                {name}
              </p>
              <Badge config={config} />
              {item.autoAnalysisEnabled === false && (
                <span
                  className="flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                  title="Esta conversa não entra na rotina automática"
                >
                  <BellOff className="h-3 w-3" />
                  Automática pausada
                </span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {phone && phone !== name && `${phone} · `}
                {item.messageCount} mensagens · última{" "}
                {relativeTime(item.lastMessageAt)} · analisada{" "}
                {relativeTime(analysis.createdAt)}
              </span>
              <AnalysisCoverage analysis={analysis} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500 dark:text-gray-400">Prioridade</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {priority.score}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 dark:text-gray-400">Atendimento</p>
              <p className={`text-xl font-bold ${scoreColor(analysis.agentScore)}`}>
                {formatScore(analysis.agentScore)}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
          <div
            className={`h-full ${config.bar}`}
            style={{ width: `${priority.score}%` }}
          />
        </div>

        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          {analysis.summary}
        </p>

        <div className="mb-3 flex flex-wrap gap-2">
          <Badge config={describe(RISK_CONFIG, analysis.riskLevel)} />
          <Badge config={describe(SATISFACTION_CONFIG, analysis.customerSatisfaction)} />
          <Badge config={describe(RESOLUTION_CONFIG, analysis.resolutionStatus)} />
          <Badge config={describe(DIRECTION_CONFIG, analysis.conversationDirection)} />
        </div>

        {signals.length > 0 && (
          <div className="mb-3 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
              <TriangleAlert className="h-3.5 w-3.5" />
              Sinais de insatisfação ({signals.length})
            </p>
            <ul className="space-y-1">
              {signals.slice(0, expanded ? signals.length : 2).map((signal, index) => (
                <li key={index} className="text-sm text-red-800 dark:text-red-200">
                  • {signal}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            {expanded ? "Menos detalhes" : "Ver análise completa"}
          </button>

          {onViewConversation && (
            <button
              onClick={() => onViewConversation(item)}
              className="flex items-center gap-1 rounded-lg bg-green-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-green-600"
            >
              <MessageSquare className="h-4 w-4" />
              Ver conversa
            </button>
          )}

          {onOpenConversation && (
            <button
              onClick={() => onOpenConversation(item.conversationId)}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              title="Abre no chat, com envio de mensagem"
            >
              <ExternalLink className="h-4 w-4" />
              Abrir no chat
            </button>
          )}

          {onToggleAutoAnalysis && (
            <button
              onClick={() =>
                onToggleAutoAnalysis(item.conversationId, !item.autoAnalysisEnabled)
              }
              disabled={togglingAutoAnalysis}
              className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm transition-colors disabled:opacity-60 ${
                item.autoAnalysisEnabled
                  ? "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                  : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
              }`}
              title={
                item.autoAnalysisEnabled
                  ? "Deixa de reanalisar esta conversa a cada nova mensagem"
                  : "Volta a analisar esta conversa automaticamente"
              }
            >
              {item.autoAnalysisEnabled ? (
                <BellOff className="h-4 w-4" />
              ) : (
                <Bell className="h-4 w-4" />
              )}
              {item.autoAnalysisEnabled ? "Pausar automática" : "Retomar automática"}
            </button>
          )}

          {onReanalyze && (
            <button
              onClick={() => onReanalyze(item.conversationId)}
              disabled={reanalyzing}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              title="Refaz a análise desta conversa com as mensagens mais recentes"
            >
              <RefreshCw className={`h-4 w-4 ${reanalyzing ? "animate-spin" : ""}`} />
              {reanalyzing ? "Analisando..." : "Reanalisar"}
            </button>
          )}

          {item.assignedUsers?.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <User className="h-3.5 w-3.5" />
              {item.assignedUsers.map((user) => user.name).join(", ")}
            </span>
          )}
        </div>
      </div>

      {expanded && (
        <div className="grid grid-cols-1 gap-4 border-t border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50 md:grid-cols-2">
          <List
            title="Próximas ações recomendadas"
            items={actions}
            icon={Target}
            tone="text-green-600 dark:text-green-400"
          />

          {topics.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Assuntos
              </p>
              <div className="flex flex-wrap gap-1.5">
                {topics.map((topic, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-blue-100 px-2.5 py-1 text-xs text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Cliente (nota {formatScore(analysis.customerScore)})
            </p>
            <dl className="space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
              <div>
                <dt className="inline font-medium">Tom: </dt>
                <dd className="inline">{analysis.customerBehavior?.tone}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Cooperação: </dt>
                <dd className="inline">{analysis.customerBehavior?.cooperation}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Evolução: </dt>
                <dd className="inline">{analysis.customerBehavior?.evolution}</dd>
              </div>
            </dl>
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Atendimento (nota {formatScore(analysis.agentScore)})
            </p>
            <dl className="space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
              <div>
                <dt className="inline font-medium">Clareza: </dt>
                <dd className="inline">{analysis.agentBehavior?.clarity}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Empatia: </dt>
                <dd className="inline">{analysis.agentBehavior?.empathy}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Conduta: </dt>
                <dd className="inline">{analysis.agentBehavior?.conduct}</dd>
              </div>
            </dl>
            {agents.length > 0 && (
              <ul className="mt-2 space-y-1">
                {agents.map((agent, index) => (
                  <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{agent.name}</span>{" "}
                    <span className={scoreColor(agent.score)}>
                      {formatScore(agent.score)}
                    </span>{" "}
                    — {agent.conduct}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(analysis.customerBehavior?.evidence?.length > 0 ||
            analysis.agentBehavior?.evidence?.length > 0) && (
            <div className="md:col-span-2">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Evidências citadas pela IA
              </p>
              <ul className="space-y-1">
                {[
                  ...(analysis.customerBehavior?.evidence || []),
                  ...(analysis.agentBehavior?.evidence || []),
                ].map((evidence, index) => (
                  <li
                    key={index}
                    className="border-l-2 border-gray-300 pl-2 text-sm italic text-gray-600 dark:border-gray-600 dark:text-gray-400"
                  >
                    “{evidence}”
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PriorityCard;
